import React, { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import cx from 'clsx';
import Pagination from 'antd/es/pagination';
import { useTranslation } from 'react-i18next';

import { Loading, LoadFailed, EmptyRecord } from '@/components';

import { TableProps } from './types';
import SortIcon from './SortIcon';
import Filter from '../DropdownFilter';
import { useSort, useFilter, usePagination } from './hook';
import { Row } from './Row';
import './index.css';

const SKELETON_ARR = Array(6).fill(undefined);

export const Table = memo(
  <T extends Record<string, any>>({
    columns,
    subRowColumns,
    showEmpty,
    showReload,
    isError,
    reload,
    data,
    showHeader = true,
    rowKey,
    rowRender,
    className,
    pagination,
    multiSort,
    sortBy,
    scrollTopOnPageChange = true,
    loading,
    expandedRowRender,
    expandedRowKeys,
    customChildren,
    expandedIconPosition = 'left',
    onExpandChange,
    onRowClick,
    skeleton,
    bodyHeight,
    pageSize,
    onSort,
    disableInnerSort,
    emptyTitle,
    emptyTitleString,
    hideEndBorder,
  }: TableProps<T>) => {
    const { t } = useTranslation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollBarRef = useRef<HTMLDivElement>(null);
    const [_data, setData] = useState(data);
    const { filterState, handleFilter, filtedData } = useFilter(columns, _data);
    const { sortState, handleSort, sortedData } = useSort({
      columns,
      sortBy,
      originData: filtedData,
      multiSort,
      onSort,
      disableInnerSort,
    });
    const [_expandedRowKeys, setExpandedRowKeys] = useState(expandedRowKeys || []);
    const [panLeft, setPanLeft] = useState(false);
    const { pagedData, paginationConfig } = usePagination({
      scrollContainerRef,
      scrollTopOnPageChange,
      pageSize,
      pagination,
      sortedData,
    });

    const handleExpandChange = useCallback(
      (collapsed: boolean, row: T, index: number) => {
        if (!collapsed) {
          const idx = _expandedRowKeys.findIndex((x, i) => {
            if (rowKey) {
              return row[rowKey] === x;
            } else {
              return index === x;
            }
          });

          if (idx > -1) {
            _expandedRowKeys.splice(idx, 1);
          }
        } else {
          _expandedRowKeys.push(rowKey ? row[rowKey] : index);
        }

        const tmpExpandedRows = rowKey
          ? data!.filter(x => _expandedRowKeys.includes(x[rowKey]))
          : _expandedRowKeys.map(key => data![key as number]);

        setExpandedRowKeys(_expandedRowKeys.slice());
        onExpandChange?.(collapsed, tmpExpandedRows);
      },
      [_expandedRowKeys, data, onExpandChange, rowKey],
    );

    useEffect(() => {
      setData(data);
    }, [data]);

    const handleHeaderItemClick = useCallback(
      ({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
        const {
          dataset: { idx },
        } = currentTarget;

        handleSort(columns[Number(idx)]);
        // collapse rows after sorted
        setExpandedRowKeys([]);
        onExpandChange?.(false, []);
      },
      [columns, handleSort, onExpandChange],
    );

    const handleScroll = useCallback(
      e => {
        const scrollLeft = e.target.scrollLeft || 0;

        setPanLeft(!!scrollLeft);

        if (scrollContainerRef.current) {
          const scrollPercent = scrollLeft / e.target.scrollWidth;

          scrollContainerRef.current.scrollLeft =
            scrollPercent * scrollContainerRef.current.scrollWidth;
        }
      },
      [setPanLeft],
    );

    const handleFilterChange = useCallback(
      (filterIdx, columnIdx) => {
        handleFilter(columns[columnIdx], filterIdx);
        // collapse rows after sorted
        setExpandedRowKeys([]);
        onExpandChange?.(false, []);
      },
      [columns, handleFilter, onExpandChange],
    );

    const scrollStyle = useMemo(() => {
      if (!bodyHeight) {
        return;
      }

      const _style: React.CSSProperties = {};
      if (bodyHeight) {
        _style.maxHeight = bodyHeight;
      }

      return _style;
    }, [bodyHeight]);

    useEffect(() => {
      if (
        scrollContainerRef.current &&
        scrollContainerRef.current.firstElementChild &&
        scrollBarRef.current
      ) {
        scrollBarRef.current.style.width = `${scrollContainerRef.current.firstElementChild?.getBoundingClientRect().width}px`;
      }
    }, []);

    return (
      <div className={cx('db-table', className)}>
        <div className={cx('db-table-main', panLeft && 'is-panLeft')} ref={scrollContainerRef}>
          {showHeader ? (
            <div className={cx('db-table-header', !!bodyHeight && 'is-sticky')}>
              {expandedRowRender && expandedIconPosition !== 'right' ? (
                <div className="db-table-expandBtnWrap" />
              ) : null}
              {columns.map((column, i) => (
                <div
                  className={cx(
                    'db-table-headerItem',
                    column.sortable && 'is-sortable',
                    column.sticky && 'is-sticky',
                    column.align && `is-${column.align}`,
                  )}
                  key={column.key || i}
                  data-idx={i}
                  onClick={handleHeaderItemClick}
                  style={{ width: column.width }}>
                  {column.title}
                  {column.sortable && column.key && (
                    <SortIcon className="db-table-headerSortIcon" order={sortState?.[column.key]} />
                  )}
                  {column.filters && column.key ? (
                    <Filter
                      filters={column.filters}
                      onFilterChange={handleFilterChange}
                      columnIdx={i}
                      currentFilterIdx={filterState[column.key]?.[0]}
                    />
                  ) : null}
                </div>
              ))}
              {expandedRowRender && expandedIconPosition === 'right' ? (
                <div className="db-table-expandBtnWrap db-table-headerItem" />
              ) : null}
            </div>
          ) : null}
          <div
            className={cx('db-table-body', hideEndBorder && 'is-noEndBorder')}
            style={scrollStyle}>
            {loading &&
              (skeleton ? (
                SKELETON_ARR?.map((row, i) => (
                  <Row key={i} columns={columns} row={row} index={i} skeleton={true} />
                ))
              ) : (
                <div className="db-table-loading">
                  <Loading />
                </div>
              ))}
            {!loading &&
              pagedData?.map((row, i) => (
                <Row
                  key={rowKey ? `${row[rowKey]}` : i}
                  columns={columns}
                  row={row}
                  index={i}
                  rowRender={rowRender}
                  subRowColumns={subRowColumns}
                  expandedRowRender={expandedRowRender}
                  expanded={_expandedRowKeys.includes(rowKey ? `${row[rowKey]}` : i)}
                  onExpandChange={handleExpandChange}
                  expandedIconPosition={expandedIconPosition}
                  onRowClick={onRowClick}
                />
              ))}
            {customChildren}
            {!(showReload && isError) && showEmpty && !loading && !data?.length ? (
              <div className="db-table-empty">
                {emptyTitle || (
                  <EmptyRecord
                    className="db-table-empty-icon"
                    content={emptyTitleString ? emptyTitleString : t('AssetList.1pg08fd')}
                  />
                )}
              </div>
            ) : null}
            {showReload && reload && isError ? (
              <LoadFailed reload={reload} loading={loading} />
            ) : null}
          </div>
        </div>
        <div className="db-table-sticky-bar" onScroll={handleScroll}>
          <div className="db-table-scrollbar" ref={scrollBarRef} />
        </div>
        {(pageSize || pagination) && !!data?.length && !isError ? (
          <Pagination className="db-talbe-pagination" size="small" {...paginationConfig} />
        ) : null}
      </div>
    );
  },
);
